import BaseController from "confinus_ui/base_controller";
import {
  addParametersToQueryString,
  removeParameterFromQueryString,
} from "lib/util/add_parameter_to_url";
import { Keys, keyWasPressed } from "lib/util/keyboard_key";
import flatpickr from "flatpickr";
import { Instance } from "flatpickr/dist/types/instance";
import { Controller } from "@hotwired/stimulus";
// NOTE: Migrated to Decor::Suite (strangler pattern); Decor controllers self-register.
import {
  DecorSuiteDropdownIdentifier,
  DecorSuiteFormsCheckboxIdentifier,
  DecorSuiteFormsSelectIdentifier,
  DecorSuiteFormsTextFieldIdentifier,
} from "decor";
import { TextFieldController } from "decor";

interface SuiteDropdownController extends Controller {
  toggle(): void;
  show(): void;
  hide(): void;
}

interface SuiteCheckboxController extends Controller {
  name: string;
  checked: boolean;
}

interface SuiteSelectController extends Controller {
  name: string;
  value: string;
}

export default class SearchAndFilterController extends BaseController {
  public static readonly targets = [
    "searchInput",
    "applyButton",
    "clearFiltersButton",
  ];

  declare public readonly searchInputTarget: HTMLInputElement;
  declare public readonly hasSearchInputTarget: boolean;
  declare public readonly applyButtonTarget: HTMLButtonElement;
  declare public readonly hasApplyButtonTarget: boolean;
  declare public readonly clearFiltersButtonTarget: HTMLButtonElement;
  declare public readonly hasClearFiltersButtonTarget: boolean;

  public static readonly outlets = [
    DecorSuiteFormsCheckboxIdentifier,
    DecorSuiteFormsSelectIdentifier,
    DecorSuiteFormsTextFieldIdentifier,
    DecorSuiteDropdownIdentifier,
  ];
  declare private readonly decorSuiteDropdownOutlet: SuiteDropdownController;
  declare private readonly decorSuiteFormsCheckboxOutlets: SuiteCheckboxController[];
  declare private readonly decorSuiteFormsSelectOutlets: SuiteSelectController[];
  declare private readonly decorSuiteFormsTextFieldOutlets: TextFieldController[];
  private get dropdownController() {
    return this.decorSuiteDropdownOutlet;
  }
  private get checkboxControllers() {
    return this.decorSuiteFormsCheckboxOutlets;
  }
  private get selectControllers() {
    return this.decorSuiteFormsSelectOutlets;
  }
  private get textFieldControllers() {
    return this.decorSuiteFormsTextFieldOutlets;
  }

  protected filters: { [key: string]: string | undefined } = {};

  public toggle(event: Event) {
    this.dropdownController.toggle();
    event.stopPropagation();
  }

  public hide() {
    this.dropdownController.hide();
  }

  public hideOnClickOutside(_event?: Event) {
    this.dropdownController.hide();
  }

  public handleClearFilters() {
    this.getFilterValues();
    if (this.hasClearFiltersButtonTarget) {
      this.clearFiltersButtonTarget.disabled = true;
    }
    // Clear the filters
    this.filters = Object.entries(this.filters).reduce<any>(
      (acc, [name, _value]) => {
        acc[name] = undefined;
        return acc;
      },
      {},
    );
    this.reloadAndSetQueryParams();
  }

  public handleApply() {
    this.getFilterValues();
    if (this.hasApplyButtonTarget) {
      this.applyButtonTarget.disabled = true;
    }
    this.reloadAndSetQueryParams();
  }

  protected getFilterValues() {
    if (this.hasSearchInputTarget && this.searchInputTarget.value) {
      const { name, value } = this.searchInputTarget;
      this.addFilterParam(name, value);
    }
    this.checkboxControllers.forEach((controller) => {
      this.addFilterParam(
        controller.name,
        controller.checked ? "true" : undefined,
      );
    });
    this.selectControllers.forEach((controller) => {
      this.addFilterParam(controller.name, controller.value);
    });
    this.textFieldControllers.forEach((controller) => {
      this.addFilterParam(controller.name, controller.value);
    });
  }

  public handleRangePicker(evt: Event) {
    const datePicker = flatpickr(evt.target as HTMLInputElement, {
      mode: "range",
      onClose: (selectedDates: Date[], dateStr: string, instance: Instance) => {
        if (selectedDates.length == 2) {
          this.addFilterParam(
            "custom_range",
            selectedDates
              .map((d) => {
                return instance.formatDate(new Date(d), "Y-m-d");
              })
              .join("_"),
          );
        }
      },
    });
    datePicker.open();
  }

  // https://medium.com/@uistephen/keyboardevent-key-for-cross-browser-key-press-check-61dbad0a067a
  public handleSearchInputKeydown(evt: KeyboardEvent) {
    if (evt.defaultPrevented) {
      return;
    }
    const enterPressed = keyWasPressed(evt, Keys.Enter);
    if (enterPressed) {
      // Stop form submission
      evt.preventDefault();

      this.searchInputTarget.disabled = true;
      this.handleApply();
    }
  }

  protected addFilterParam(name: string, value?: string) {
    this.filters[name] = value;
  }

  protected reloadAndSetQueryParams() {
    // Reset to page 1
    this.filters["page"] = undefined;
    window.location.search = Object.entries(this.filters).reduce(
      (params, filter) => {
        const [name, value] = filter;
        if (value === undefined) {
          return removeParameterFromQueryString(params, name);
        } else {
          return addParametersToQueryString(params, {
            [name]: value,
          });
        }
      },
      window.location.search,
    );
  }
}
